import { useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'hst-demo-session-id';

/**
 * NOTE on storage choice (31/05/2026 — code review answer):
 *   The session id is a random throwaway token tied to a specific
 *   browser/device — it carries NO authentication, NO PII, NO secret.
 *   Its sole job is to deduplicate funnel events across reloads so we
 *   don't double-count the same visitor. localStorage is the correct
 *   tool here: sessionStorage would lose the id at tab close (defeating
 *   the deduplication intent across days), and encryption would add zero
 *   security since there is nothing sensitive to protect.
 *   Same reasoning as `useBankruptcyReporter` / `InternationalSwitchModal`.
 */
function ensureSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `s-fallback-${Date.now()}`;
  }
}

/**
 * Demo funnel tracker — one POST per (sessionId, event) thanks to the
 * server-side upsert. Failures are silent (analytics must never block UX).
 */
export default function useDemoTracker() {
  const fired = useRef(new Set());

  const track = useCallback(async (event, source = 'demo') => {
    if (fired.current.has(event)) return;
    fired.current.add(event);
    const sessionId = ensureSessionId();
    try {
      await fetch(`${API}/api/demo/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, session_id: sessionId, source }),
        keepalive: true,
      });
    } catch (err) {
      // Analytics is best-effort. Swallow.
      if (process.env.NODE_ENV === 'development') {
        console.warn('[demoTracker] event failed:', err.message);
      }
    }
  }, []);

  return { track };
}
